import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import styles from "./HealthSummary.module.css";

const HealthSummary: React.FC = () => {
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const [progress, setProgress] = useState(0);
  const { navigateBackIfExists } = useNavigationSource();

  // Auto-start loading and progress animation on mount
  useEffect(() => {
    const duration = 3000; // 3 seconds
    const intervalTime = 30; // Update every 30ms for smooth animation
    const increment = (100 / duration) * intervalTime;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(interval);
          // Navigate when progress reaches 100%
          setTimeout(() => {
            navigate("/health-summary/details");
          }, 100);
          return 100;
        }
        return next;
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [navigate]);

  const handleBack = () => {
    if (!navigateBackIfExists()) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6">
      <MHeader
        module="Health Summary"
        subHeading="Health summary"
        memberInfo={memberInfo}
        description="View your health summary here for better care decisions. It grows richer as you add more memories and records."
        showGradient
        onBack={handleBack}
      />

      <MSpacer mt={32} />

      <div className={styles.loaderContainer}>
        {/* Circular Progress Loader */}
        <div className={styles.progressWrapper}>
          {/* Background Circle */}
          <svg
            width="109"
            height="109"
            viewBox="0 0 109 109"
            className={styles.progressSvg}
          >
            <circle
              cx="54.5"
              cy="54.5"
              r="50"
              className={styles.progressCircleBackground}
            />
            {/* Progress Circle */}
            <circle
              cx="54.5"
              cy="54.5"
              r="50"
              className={styles.progressCircle}
              strokeDasharray={`${2 * Math.PI * 50}`}
              strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress / 100)}`}
              transform="rotate(-90 54.5 54.5)"
            />
          </svg>
          {/* Percentage Text in Center */}
          <div className={styles.percentageText}>
            {Math.max(1, Math.round(progress))}%
          </div>
        </div>

        {/* Text below loader */}
        <div className={styles.preparingText}>
          Preparing your health summary
        </div>
        <div className={styles.preparingSubText}>
          Recently added documents may take<br />a few minutes to reflect
        </div>
      </div>
    </div>
  );
};

export default HealthSummary;
